import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";
import { trackEvent } from "@/lib/track";

/** Dedicated URL so Google Tag Manager / GA4 can fire a conversion on a real pageview. */
export default function EnrolThankYouPage() {
  const settings = useSiteSettings();
  const term = settings.term_label;

  const fired = useRef(false);

  useEffect(() => {
    // A lead is a conversion — fire it exactly once per page view
    if (fired.current) return;
    fired.current = true;
    trackEvent("enrolment_submitted", { form_name: "term_enrolment", form_destination: "/enrol/thank-you" });
    trackEvent("generate_lead", { content_name: "Term Enrolment", content_category: "Enrolment", form_name: "term_enrolment" });
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="enrol-thankyou-page">
      <Seo title="Thank you — enrolment received" description="We'll call you within 24 hours." path="/enrol/thank-you" noindex />
      <Navbar />

      <section className="hero-gradient dark:bg-transparent pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto text-center animate-fade-in-up">
          <div className="w-20 h-20 rounded-full bg-brand-gold flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle2 className="h-10 w-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4" data-testid="thankyou-heading">
            Thank you for submitting
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 font-body mb-3">
            Your {term} enrolment enquiry is in. We read every single one personally.
          </p>
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-purple-soft dark:bg-brand-purple/20 px-5 py-2.5 mb-8">
            <Phone className="h-4 w-4 text-brand-purple dark:text-brand-purple-light" />
            <span className="font-body text-sm font-semibold text-brand-purple dark:text-brand-purple-light">
              We'll call you within 24 hours
            </span>
          </div>

          <div className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-6 text-left mb-8">
            <p className="font-heading text-base font-bold text-slate-900 dark:text-white mb-3">What happens next</p>
            <ol className="space-y-2.5 text-sm text-slate-600 dark:text-slate-400 font-body list-decimal list-inside">
              <li>We review your answers and match you to the right small group.</li>
              <li>A quick call to confirm the fit, your goals and a class time.</li>
              <li>You're enrolled — resources and your Google Classroom access follow straight after.</li>
            </ol>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/pricing">
              <Button className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full px-8 py-6 font-bold font-body" data-testid="thankyou-pricing-btn">
                See pricing <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/blog">
              <Button variant="outline" className="rounded-full px-8 py-6 font-bold font-body border-brand-purple text-brand-purple hover:bg-brand-purple-soft" data-testid="thankyou-blog-btn">
                Read our study tips
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
